import React, { useState, useEffect } from 'react';
import { X, Play, CheckCircle, ArrowRight, Rocket } from 'lucide-react';
import { Button } from './button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OnboardingVideoPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [onboardingVideo, setOnboardingVideo] = useState(null);
  const [hasWatched, setHasWatched] = useState(false);

  useEffect(() => {
    // Check if this is a first-time user
    checkFirstTimeUser();
  }, []);

  const checkFirstTimeUser = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('rooftrack_user') || '{}');
      if (!user?.id) return;

      // Check if user has seen onboarding
      const onboardingSeen = localStorage.getItem(`rooftrack_onboarding_${user.id}`);
      
      if (!onboardingSeen) {
        // Fetch the onboarding video
        const response = await axios.get(`${BACKEND_URL}/api/demo-videos/public`);
        const videos = response.data.videos || [];
        
        // Find the "getting_started" video
        const gettingStartedVideo = videos.find(v => 
          v.feature_key === 'getting_started' || 
          v.title?.toLowerCase().includes('getting started')
        );
        
        if (gettingStartedVideo) {
          setOnboardingVideo(gettingStartedVideo);
          // Show popup after a short delay
          setTimeout(() => setIsVisible(true), 1500);
        }
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    
    // Mark onboarding as seen
    const user = JSON.parse(localStorage.getItem('rooftrack_user') || '{}');
    if (user?.id) {
      localStorage.setItem(`rooftrack_onboarding_${user.id}`, JSON.stringify({
        seen_at: new Date().toISOString(),
        watched_video: hasWatched
      }));
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleVideoEnd = () => {
    setHasWatched(true);
    setIsPlaying(false);
  };

  if (!isVisible || !onboardingVideo) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center space-x-3 mb-2">
            <Rocket className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Welcome to HomeFixer360!</h2>
          </div>
          <p className="text-orange-100">
            Watch this quick video to get started in just 3 easy steps.
          </p>
        </div>

        {/* Video Area */}
        <div className="p-6">
          {!isPlaying ? (
            <div 
              className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center cursor-pointer group relative overflow-hidden"
              onClick={() => setIsPlaying(true)}
            >
              {onboardingVideo.thumbnail_url ? (
                <img 
                  src={onboardingVideo.thumbnail_url} 
                  alt="Getting Started" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-2">Your First 3 Steps</div>
                </div>
              )}
              
              {/* Play Button Overlay */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-10 h-10 text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              {onboardingVideo.video_url ? (
                <video 
                  src={onboardingVideo.video_url}
                  controls
                  autoPlay
                  onEnded={handleVideoEnd}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p>Video coming soon...</p>
                </div>
              )}
            </div>
          )}

          {/* Quick Steps Summary */}
          <div className="mt-6 space-y-3">
            <h3 className="font-semibold text-slate-800">Your First 3 Steps:</h3>
            <div className="grid gap-3">
              <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                <div>
                  <div className="font-medium text-slate-800">Set Up Your Rates</div>
                  <div className="text-sm text-slate-600">Configure your pricing for accurate estimates</div>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                <div>
                  <div className="font-medium text-slate-800">Create Your Lead Page</div>
                  <div className="text-sm text-slate-600">Build a branded landing page to capture leads</div>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                <div>
                  <div className="font-medium text-slate-800">Test an Estimate</div>
                  <div className="text-sm text-slate-600">Verify your pricing is accurate</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          <button 
            onClick={handleSkip}
            className="text-slate-500 hover:text-slate-700 text-sm"
          >
            Skip for now
          </button>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              {hasWatched ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  Got It!
                </>
              ) : (
                "I'll watch later"
              )}
            </Button>
            <Button
              onClick={() => {
                handleClose();
                window.location.href = '/dashboard/rates';
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Go to My Rates
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingVideoPopup;
